import { fetchData } from 'Utils/fetch-data';
import type { NewsI, NewsFeedItemI, NewsDraftI, PaginationMetaI } from 'types';

// `baseURL` ya incluye `/api`, así que aquí solo va el recurso.
const ENDPOINT = '/news';

export interface NewsFeedQueryI {
  page?: number;
  limit?: number;
}

export interface NewsAdminQueryI extends NewsFeedQueryI {
  /** iLike sobre el título; se omite si viene vacío. */
  search?: string;
  /** Filtro explícito: true/false filtran, undefined no manda el param. */
  pinned?: boolean;
}

export interface NewsListI<T> {
  items: T[];
  pagination: PaginationMetaI | null;
}

type NewsListResponseI<T> = { data: NewsListI<T> | null; error: string | null };
type NewsResponseI = { data: NewsI | null; error: string | null };

/**
 * Los errores del back llegan en español en el envelope `{error, details}` y
 * `fetchData` ya los normaliza en `error`; aquí se propagan tal cual para que
 * la UI (drawer/tabla) los muestre. `FETCH_ERROR` solo aparece cuando la
 * respuesta llega malformada sin mensaje del back.
 */
const listRequest = async <T>(url: string, params: Record<string, unknown>): Promise<NewsListResponseI<T>> => {
  const { data, error, pagination } = await fetchData<T[]>(url, {
    method: 'GET',
    params,
    includePagination: true,
  });
  if (error !== null) return { data: null, error };
  if (!Array.isArray(data)) return { data: null, error: 'FETCH_ERROR' };
  return { data: { items: data, pagination: pagination ?? null }, error: null };
};

const itemRequest = async (url: string, options: Parameters<typeof fetchData>[1]): Promise<NewsResponseI> => {
  const { data, error } = await fetchData<NewsI>(url, options);
  if (error !== null) return { data: null, error };
  if (data == null) return { data: null, error: 'FETCH_ERROR' };
  return { data, error: null };
};

/** GET /news/feed — publicadas y no expiradas, orden pinned DESC, publishAt DESC. Solo JWT. */
export const getNewsFeed = (query: NewsFeedQueryI = {}): Promise<NewsListResponseI<NewsFeedItemI>> => {
  const params: Record<string, unknown> = {};
  if (query.page != null) params.page = query.page;
  if (query.limit != null) params.limit = query.limit;
  return listRequest<NewsFeedItemI>(`${ENDPOINT}/feed`, params);
};

/** GET /news — listado admin (incluye programadas/expiradas). Requiere `news.read`. */
export const getNewsAdmin = (query: NewsAdminQueryI = {}): Promise<NewsListResponseI<NewsI>> => {
  const params: Record<string, unknown> = {};
  if (query.page != null) params.page = query.page;
  if (query.limit != null) params.limit = query.limit;
  if (query.search) params.search = query.search;
  if (query.pinned !== undefined) params.pinned = query.pinned;
  return listRequest<NewsI>(ENDPOINT, params);
};

/** GET /news/:id — detalle con `createdBy`. Requiere `news.read`. */
export const getNewsById = (id: string): Promise<NewsResponseI> =>
  itemRequest(`${ENDPOINT}/${id}`, { method: 'GET' });

/** POST /news — alta. Requiere `news.create`. */
export const createNews = (draft: NewsDraftI): Promise<NewsResponseI> =>
  itemRequest(ENDPOINT, { method: 'POST', data: draft });

/** PATCH /news/:id — actualización parcial. Requiere `news.update`. */
export const updateNews = (id: string, partial: Partial<NewsDraftI>): Promise<NewsResponseI> =>
  itemRequest(`${ENDPOINT}/${id}`, { method: 'PATCH', data: partial });

/**
 * DELETE /news/:id — borra la noticia y sus archivos S3. Requiere `news.delete`.
 * El back responde 204 sin body: axios resuelve con body vacío y `fetchData`
 * regresa `{ data: undefined, error: null }` (el unwrap `response.data?.data`
 * da undefined, no truena). Por eso el éxito se decide por `error === null`,
 * nunca por la presencia de `data`.
 */
export const deleteNews = async (id: string): Promise<{ data: true | null; error: string | null }> => {
  const { error } = await fetchData<never>(`${ENDPOINT}/${id}`, { method: 'DELETE' });
  if (error !== null) return { data: null, error };
  return { data: true, error: null };
};
